<script lang="ts" setup>
import { ref, computed, watch } from 'vue'
import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
  ComboboxButton,
} from '@headlessui/vue'

import IconCross from '~/assets/icon/general/Cross.svg'
import IconCheck from '~/assets/icon/general/Check.svg'
import { normalize } from '../../utils/normalize'

const props = defineProps({
  autocomplete: Boolean,
  searchCollection: String,
  searchFields: Array,
  searchInclude: Array,
  values: Object,
  model: String,
  defaultInput: String,
  displayFunction: Function,
  keyValue: {
    type: String,
    default: 'id',
  },
  filterBy: {
    type: String,
    required: false,
  },
})

const input = ref(props.defaultInput)
const selectedResult = ref(null)
const data = ref(null)
const pending = ref(false)
const error = ref(null)

const query = computed(() => ({
  q: input.value ?? '*',
  fields: props.searchFields.join('-'),
  include: props.searchInclude.join('-'),
  filter_by: props.filterBy,
  limit: 6,
}))

async function fetchData() {
  pending.value = true
  error.value = null
  try {
    const result = await execSearch(props.searchCollection, query.value)
    data.value = result
    if (props.defaultInput && data.value.documents.length > 0) {
      const firstResult = data.value.documents[0]
      input.value = formatResult(firstResult)
      props.values[props.model] = firstResult.document[props.keyValue]
    }
  } catch (err) {
    error.value = err
  } finally {
    pending.value = false
  }
}

const search = computed(() => input.value || selectedResult.value || '')

const fetchResults = computed(() => {
  if (!data.value || pending.value || error.value) return []
  return Array.from(
    new Set(data.value.documents.map((doc) => formatResult(doc)))
  )
})

const autocompleteResults = computed(() => {
  const normalizedInput = normalize(input.value?.toLowerCase() ?? '')
  return input.value === ''
    ? fetchResults.value
    : fetchResults.value.filter((result) => {
        return normalize(result.toLowerCase()).includes(normalizedInput)
      })
})

function formatResult(doc) {
  return props.displayFunction(doc.document ?? doc)
}

watch(input, () => {
  fetchData()
})

watch(selectedResult, (newValue) => {
  if (newValue) {
    const value = data.value.documents.find(
      (doc) => formatResult(doc) === newValue
    )
    if (value) {
      input.value = formatResult(value)
      props.values[props.model] = value.document[props.keyValue]
    }
  }
})

function isUUID(uuid) {
  let s = '' + uuid

  s = s.match(
    '^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$'
  )
  if (s === null) {
    return false
  }
  return true
}

watch(
  () => props.defaultInput,
  (newDefaultInput, oldDefaultInput) => {
    if (newDefaultInput && !isUUID(newDefaultInput)) {
      input.value = newDefaultInput
      fetchData()
    } else if (!newDefaultInput && oldDefaultInput) {
      selectedResult.value = null
      input.value = ''
    }
  },
  { immediate: true }
)

const clearSearchInput = () => {
  selectedResult.value = null
  input.value = ''
  props.values[props.model] = null
}

function onChange(event) {
  input.value = event.target.value
  if (event.target.value === '') selectedResult.value = null
}

function getSelected(result) {
  selectedResult.value = result
}
</script>

<template>
  <div class="group relative font-normal">
    <Combobox v-if="autocomplete" v-model="search">
      <ComboboxButton class="w-full" @click="fetchData">
        <ComboboxInput
          class="w-full border border-shade-900 pr-20 pl-8 text-md transition-colors placeholder:capitalize placeholder:text-shade-alpha-900 hover:border-primary-400 focus:border-primary-400 focus:ring focus:ring-primary-alpha-400 focus-visible:outline-none"
          :autocomplete="false"
          @change="onChange"
        />
      </ComboboxButton>

      <transition
        enter-active-class="transition duration-100 ease-out"
        enter-from-class="transform scale-95 -translate-y-4 opacity-0"
        enter-to-class="transform scale-100 translate-y-0 opacity-100"
        leave-active-class="transition duration-75 ease-in"
        leave-from-class="transform scale-100 translate-y-0 opacity-100"
        leave-to-class="transform scale-95 -translate-y-4 opacity-0"
      >
        <ComboboxOptions
          class="absolute z-20 mt-1 flex w-full flex-col border border-shade-900 bg-shade-50 p-3 shadow-sm"
        >
          <div
            v-if="pending"
            class="relative cursor-default select-none text-shade-600"
          >
            {{ $t('labels.loading') }}
          </div>
          <div
            v-if="autocompleteResults.length === 0 && input !== ''"
            class="relative cursor-default select-none text-shade-600"
          >
            {{ $t('labels.nothing_found') }}
          </div>
          <ComboboxOption
            v-for="result in autocompleteResults"
            :key="result"
            :value="result"
            @click="getSelected(result)"
          >
            <li
              class="relative cursor-pointer select-none"
              :class="{
                'bg-primary-alpha-100 text-primary-400': selected,
                'bg-shade-alpha-300': active && !selected,
              }"
            >
              <span
                class="block truncate"
                :class="{ 'font-medium': selected, 'font-normal': !selected }"
              >
                {{ result }}
              </span>
              <span
                v-if="selected"
                class="absolute inset-y-0 left-0 flex items-center pl-2 text-primary-400"
              >
                <IconCheck class="h-5 w-5" aria-hidden="true" />
              </span>
            </li>
          </ComboboxOption>
        </ComboboxOptions>
      </transition>
    </Combobox>
    <div class="absolute inset-y-0 right-4 flex items-center gap-3">
      <button
        v-if="input !== ''"
        @click.prevent="clearSearchInput"
        class="flex h-4 w-4 items-center justify-center rounded-full bg-shade-200 hover:bg-shade-900 transition-colors duration-200 ease-in-out"
      >
        <IconCross class="h-3 w-3 text-shade-50" />
      </button>
    </div>
  </div>
</template>
